import type { DataEditorRef } from "@glideapps/glide-data-grid";
import { type RefObject, useCallback, useEffect, useMemo } from "react";
import { buildCellCommentIndex } from "@/payroll/ai-script/cell-comments/cell-comment-index";
import { useCellCommentThreads } from "@/payroll/ai-script/cell-comments/query";
import type { IAiSpreadsheetCell } from "@/payroll/ai-script/types";
import { getAiSpreadsheetCellDisplayValue } from "./cell-display-value";
import { getSavedSpreadsheetRow } from "./spreadsheet-region-view";
import type { ViewerActiveSheet } from "./use-viewer-active-sheet";
import type { ViewerUiState } from "./use-viewer-ui-state";
import { formatCoordinate } from "./utils";

interface UseViewerCommentsParams {
  businessId: string | null;
  runId: string;
  commentsEnabled: boolean;
  isReadOnlySource: boolean;
  sidebarOpen: boolean;
  gridRef: RefObject<DataEditorRef | null>;
  gridWrapperRef: RefObject<HTMLDivElement | null>;
  sheet: Pick<
    ViewerActiveSheet,
    "activeLoadedSheet" | "adaptedSheet" | "regionRowMap"
  >;
  ui: Pick<
    ViewerUiState,
    | "commentCard"
    | "setCommentCard"
    | "commentCardTick"
    | "setCommentCardTick"
    | "isMaximized"
  >;
}

export const useViewerComments = ({
  businessId,
  runId,
  commentsEnabled,
  isReadOnlySource,
  sidebarOpen,
  gridRef,
  gridWrapperRef,
  sheet: { activeLoadedSheet, adaptedSheet, regionRowMap },
  ui: {
    commentCard,
    setCommentCard,
    commentCardTick,
    setCommentCardTick,
    isMaximized,
  },
}: UseViewerCommentsParams) => {
  const areCommentsEnabled =
    commentsEnabled && !isReadOnlySource && !!businessId;
  const { data: commentThreads } = useCellCommentThreads(
    businessId,
    runId,
    areCommentsEnabled,
  );
  const commentIndex = useMemo(
    () => buildCellCommentIndex(commentThreads, activeLoadedSheet?.id ?? null),
    [activeLoadedSheet?.id, commentThreads],
  );
  const savedCellCoordinate = useCallback(
    (col: number, gridRow: number, record?: IAiSpreadsheetCell) => {
      const cell = record ?? adaptedSheet?.getCellRecord(col, gridRow);
      return cell
        ? { row: cell.row, col: cell.col }
        : {
            row: getSavedSpreadsheetRow(regionRowMap, gridRow) + 1,
            col: col + 1,
          };
    },
    [adaptedSheet, regionRowMap],
  );
  const openCommentCard = useCallback(
    (col: number, gridRow: number, focusComposer: boolean) => {
      if (!activeLoadedSheet) return;

      const record = adaptedSheet?.getCellRecord(col, gridRow);
      const saved = savedCellCoordinate(col, gridRow, record);
      const value = record
        ? getAiSpreadsheetCellDisplayValue(record).trim()
        : "";
      setCommentCard({
        sheetId: activeLoadedSheet.id,
        row: saved.row,
        col: saved.col,
        gridCol: col,
        gridRow,
        address: formatCoordinate([saved.col - 1, saved.row - 1]),
        context: value ? { value } : undefined,
        focusComposer,
      });
    },
    [activeLoadedSheet, adaptedSheet, savedCellCoordinate],
  );
  const isCommentCardOpen = commentCard !== null;
  useEffect(() => {
    const wrapper = gridWrapperRef.current;
    if (!isCommentCardOpen || !wrapper) return undefined;

    let frame = 0;
    const bump = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() =>
        setCommentCardTick((tick) => tick + 1),
      );
    };
    const settle = window.setTimeout(bump, 300);
    wrapper.addEventListener("scroll", bump, { capture: true, passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settle);
      wrapper.removeEventListener("scroll", bump, { capture: true });
    };
  }, [isCommentCardOpen, commentCard]);
  const commentCardAnchor = useMemo(() => {
    if (!commentCard) return null;
    const bounds = gridRef.current?.getBounds(
      commentCard.gridCol,
      commentCard.gridRow,
    );
    const frame = gridWrapperRef.current?.getBoundingClientRect();
    if (!bounds || !frame) return null;
    const right = bounds.x + bounds.width;
    if (
      bounds.y + bounds.height < frame.top ||
      bounds.y > frame.bottom ||
      right < frame.left ||
      bounds.x > frame.right
    ) {
      return null;
    }
    return {
      x: Math.min(right, frame.right),
      y: Math.max(bounds.y, frame.top),
    };
  }, [commentCard, commentCardTick, isMaximized, sidebarOpen]);

  return {
    areCommentsEnabled,
    commentThreads,
    commentIndex,
    savedCellCoordinate,
    openCommentCard,
    commentCardAnchor,
  };
};

export type ViewerComments = ReturnType<typeof useViewerComments>;

interface UseOpenCommentCardOnSelectParams {
  comments: ViewerComments;
  selectedCol: number | null;
  selectedRow: number | null;
  activeLoadedSheetId: string | undefined;
  ui: Pick<
    ViewerUiState,
    "rowMenu" | "pendingCommentOpenRef" | "setCommentCard"
  >;
}

export const useOpenCommentCardOnSelect = ({
  comments: {
    areCommentsEnabled,
    commentThreads,
    commentIndex,
    savedCellCoordinate,
    openCommentCard,
  },
  selectedCol,
  selectedRow,
  activeLoadedSheetId,
  ui: { rowMenu, pendingCommentOpenRef, setCommentCard },
}: UseOpenCommentCardOnSelectParams) => {
  const haveCommentThreadsLoaded = commentThreads !== undefined;
  useEffect(() => {
    if (!areCommentsEnabled || selectedCol === null || selectedRow === null) {
      return;
    }
    if (rowMenu) return;
    const saved = savedCellCoordinate(selectedCol, selectedRow);
    const pending = pendingCommentOpenRef.current;
    if (
      pending &&
      pending.sheetId === activeLoadedSheetId &&
      pending.row === saved.row &&
      pending.col === saved.col
    ) {
      pendingCommentOpenRef.current = null;
      openCommentCard(selectedCol, selectedRow, false);
      return;
    }
    if (commentIndex.hasOpenThreadAt(saved.row, saved.col)) {
      openCommentCard(selectedCol, selectedRow, false);
    } else {
      setCommentCard((current) =>
        current && (current.row !== saved.row || current.col !== saved.col)
          ? null
          : current,
      );
    }
  }, [areCommentsEnabled, haveCommentThreadsLoaded, selectedCol, selectedRow]);
};
